import type { MenuItemConfig, MenuSectionConfig } from '@app/core/services/menu-config.service'
import { COMMERCE_MODULE_ORDER, screensFor } from './commerce-registry'
import { CommerceScreenDef, CommerceScreenModule } from './commerce-screen.models'

const SECTION_ICON: Record<CommerceScreenModule, string> = {
  crm: 'handshake',
  member: 'loyalty',
  hrm: 'badge',
  bpm: 'account_tree',
  platform: 'domain',
}

/**
 * Permission ids (opa-policy module-action-permissions) granted by each commerce menu code:
 * view codes grant read, edit codes grant every write action of the module.
 */
const MODULE_PERMISSION_IDS: Record<CommerceScreenModule, { view: string[]; edit: string[] }> = {
  crm: { view: ['15001'], edit: ['15002', '15003', '15004', '15005'] },
  hrm: { view: ['20001'], edit: ['20002', '20003', '20004'] },
  member: { view: ['21001'], edit: ['21002'] },
  bpm: { view: ['22001'], edit: ['22002', '22003'] },
  platform: { view: ['20001'], edit: ['20003'] },
}

export interface CommerceMenuEntry {
  code: string
  menu: string
  submenu: string
  action: 'view' | 'edit'
  route?: string
  requirePermission: string[]
  app: 'back_office'
}

/** Sidebar sections in Figma order (Customers & Loyalty, Members, People, Workflow, Platform). */
export function buildCommerceMenuSections(): MenuSectionConfig[] {
  return COMMERCE_MODULE_ORDER.map((module) => {
    const name = `commerce_${module}`
    const items: MenuItemConfig[] = screensFor(module)
      .filter((s) => s.inMenu)
      .map((s) => ({
        name: s.id,
        label: s.titleKey,
        route: `/${module}/${s.path}`,
        path: `/${module}/${s.path}`,
        parent: name,
        firstChild: s.id,
        secondChild: null,
        permissionCode: `${s.menuCode}.view`,
        commerceModule: module === 'platform' ? undefined : module,
      }))
    return { name, title: `sidebar.commerce.${module}`, icon: SECTION_ICON[module], root: true, items }
  }).filter((section) => section.items.length > 0)
}

/** One view entry per menu code, plus an edit entry when any screen with that code writes. */
export function buildMenuManifest(screens: readonly CommerceScreenDef[]): CommerceMenuEntry[] {
  const entries: CommerceMenuEntry[] = []
  const codes = [...new Set(screens.map((s) => s.menuCode))]
  for (const code of codes) {
    const group = screens.filter((s) => s.menuCode === code)
    const module = group[0].module
    const [menu, submenu] = code.split('.')
    const home = group.find((s) => s.inMenu) ?? group[0]
    entries.push({
      code: `${code}.view`,
      menu,
      submenu,
      action: 'view',
      route: `/${module}/${home.path}`,
      requirePermission: MODULE_PERMISSION_IDS[module].view,
      app: 'back_office',
    })
    if (group.some((s) => s.writes)) {
      entries.push({
        code: `${code}.edit`,
        menu,
        submenu,
        action: 'edit',
        requirePermission: MODULE_PERMISSION_IDS[module].edit,
        app: 'back_office',
      })
    }
  }
  return entries
}
